import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateExtensionQuote, useInvalidateQuote, useQuote, useSession } from '../api/hooks';
import type { Quote } from '../api/types';
import { TopBar } from '../components/chrome';
import { DurationEditor } from '../components/duration-editor';
import { QuoteStatus } from '../components/quote-status';
import { Cta, ErrorState, Loading, Muted, Note, Row, Screen, Title } from '../components/ui';
import { useApiError } from '../i18n/useApiError';
import { countdown, durationLabel, usd } from '../lib/format';
import { lotExpiryLabel, resolveLotTimeLanguage } from '../lib/lot-time';
import { useCountdown } from '../lib/use-countdown';

export function ExtendPage() {
  const { t } = useTranslation();
  const describeError = useApiError();
  const { sessionId } = useParams({ from: '/sessions/$sessionId/extend' });
  const navigate = useNavigate();
  const session = useSession(sessionId);
  const { mutateAsync: createQuote, isPending } = useCreateExtensionQuote();
  const { mutateAsync: invalidateQuote } = useInvalidateQuote();
  const [requestedMinutes, setRequestedMinutes] = useState(60);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const sequence = useRef(0);
  const activeQuoteId = useRef<string | null>(null);

  const changeDuration = (minutes: number) => {
    if (minutes === requestedMinutes) return;
    sequence.current += 1;
    const previous = activeQuoteId.current;
    activeQuoteId.current = null;
    setQuote(null);
    setError(null);
    if (previous) void invalidateQuote(previous).catch(() => undefined);
    setRequestedMinutes(minutes);
  };

  // Duration ↔ departure is a semantic change even at the same minute count,
  // and an expired hold is equally dead: both paths must discard the old quote
  // and request a fresh one (review P0-2).
  const requote = () => {
    sequence.current += 1;
    const previous = activeQuoteId.current;
    activeQuoteId.current = null;
    setQuote(null);
    setError(null);
    if (previous) void invalidateQuote(previous).catch(() => undefined);
    setRetry((value) => value + 1);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: retry is an explicit request revision.
  useEffect(() => {
    const current = ++sequence.current;
    if (requestedMinutes < 1) return;
    const timer = window.setTimeout(() => {
      void createQuote({ sessionId, additionalMinutes: requestedMinutes })
        .then((next) => {
          if (sequence.current !== current) {
            void invalidateQuote(next.id).catch(() => undefined);
            return;
          }
          activeQuoteId.current = next.id;
          setQuote(next);
        })
        .catch((cause) => {
          if (sequence.current === current) {
            setError(describeError(cause, 'extend.loadFailed'));
          }
        });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [createQuote, describeError, invalidateQuote, requestedMinutes, retry, sessionId]);

  if (session.isPending || !session.data)
    return (
      <Page>
        <Loading />
      </Page>
    );
  const current = session.data;

  return (
    <>
      <TopBar title={t('extend.title')} />
      <Screen className="min-h-[calc(100dvh-52px)] pb-0">
        {/* The top bar already says "Extend" — the body only carries what
            the bar lacks: the stay being extended (review §5). */}
        <Muted>
          {t('extend.subtitle', {
            duration: durationLabel(current.totalMinutes, t),
            paid: usd(current.paidTotalCents),
          })}
        </Muted>
        <DurationEditor
          value={requestedMinutes}
          onChange={changeDuration}
          onModeChange={requote}
          maxMinutes={60 * 24 * 7}
          timezone={current.lot.timezone}
        />

        {quote ? (
          <div className="flex flex-col gap-3 rounded-lg bg-surface-muted p-4">
            <Row
              label={t('extend.requestedAdditional')}
              value={durationLabel(quote.additionalRequestedMinutes ?? requestedMinutes, t)}
            />
            <Row
              label={t('extend.validAdditional')}
              value={durationLabel(quote.additionalEntitlementMinutes ?? requestedMinutes, t)}
            />
            <Row label={t('extend.alreadyPaid')} value={usd(current.paidTotalCents)} mono />
            <Row label={t('extend.totalAfter')} value={usd(quote.totalCents)} mono />
          </div>
        ) : null}

        {error ? (
          <button
            type="button"
            className="rounded-lg border border-danger p-3 text-left text-[13px] text-danger"
            onClick={() => {
              setError(null);
              setRetry((value) => value + 1);
            }}
          >
            {error} · <span className="font-semibold text-accent">{t('common.retry')}</span>
          </button>
        ) : null}

        <div className="flex-1" />
        <ExtensionFooter
          quote={quote}
          currentExpiry={current.expiresAt}
          timezone={current.lot.timezone}
          loading={isPending && !quote}
          onRequote={requote}
          onReview={() =>
            quote &&
            void navigate({
              to: '/sessions/$sessionId/extend-confirm',
              params: { sessionId },
              search: { quoteId: quote.id },
            })
          }
        />
      </Screen>
    </>
  );
}

function ExtensionFooter({
  quote,
  currentExpiry,
  timezone,
  loading,
  onRequote,
  onReview,
}: {
  quote: Quote | null;
  currentExpiry: string;
  timezone: string;
  loading: boolean;
  onRequote: () => void;
  onReview: () => void;
}) {
  const { t, i18n } = useTranslation();
  const remaining = useCountdown(
    quote?.expiresAt ?? '1970-01-01T00:00:00.000Z',
    quote
      ? Math.max(
          0,
          Math.floor((new Date(quote.expiresAt).getTime() - new Date(quote.createdAt).getTime()) / 1000),
        )
      : 0,
  );
  const newExpiry = quote?.additionalEntitlementMinutes
    ? new Date(new Date(currentExpiry).getTime() + quote.additionalEntitlementMinutes * 60_000)
    : null;
  return (
    <div className="sticky bottom-0 -mx-[18px] flex flex-col gap-3 border-t border-hairline bg-white px-[18px] pb-4 pt-3">
      <div className="flex items-center gap-3">
        <div className="flex flex-1 flex-col gap-0.5">
          <Muted>{t('extend.chargeNow')}</Muted>
          {/* Quote state lives in QuoteStatus alone; this slot only ever
              describes the outcome of the extension. */}
          <span className="text-sm font-semibold">
            {newExpiry
              ? t('extend.newEndShort', {
                  time: lotExpiryLabel(newExpiry, timezone, resolveLotTimeLanguage(i18n.language), t),
                })
              : '—'}
          </span>
        </div>
        <span className="num text-3xl font-bold">{quote ? usd(quote.chargeCents) : '—'}</span>
      </div>
      <QuoteStatus quoteId={quote?.id ?? null} remaining={remaining} onRequote={onRequote} />
      <Cta
        label={t('extend.review')}
        note={quote ? t('extend.reviewNote', { amount: usd(quote.chargeCents) }) : t('duration.waitingQuote')}
        loading={loading}
        disabled={!quote || remaining <= 0}
        onClick={onReview}
      />
    </div>
  );
}

export function ExtendConfirmPage() {
  const { t, i18n } = useTranslation();
  const { sessionId } = useParams({ from: '/sessions/$sessionId/extend-confirm' });
  const { quoteId } = useSearch({ from: '/sessions/$sessionId/extend-confirm' }) as { quoteId: string };
  const navigate = useNavigate();
  const quoteQuery = useQuote(quoteId);
  const quote = quoteQuery.data?.quote;
  const session = useSession(sessionId);
  const remaining = useCountdown(
    quote?.expiresAt ?? '1970-01-01T00:00:00.000Z',
    quote
      ? Math.max(
          0,
          Math.floor((new Date(quote.expiresAt).getTime() - new Date(quote.createdAt).getTime()) / 1000),
        )
      : 0,
  );

  if (quoteQuery.isPending || session.isPending)
    return (
      <Page>
        <Loading label={t('extend.calculating')} />
      </Page>
    );
  if (quoteQuery.isError || !quote || !session.data)
    return (
      <Page>
        <ErrorState message={t('extend.loadFailed')} onRetry={() => void quoteQuery.refetch()} />
      </Page>
    );
  if (quote.status !== 'valid' || remaining <= 0) {
    return (
      <Page>
        <Title sub={t('confirm.expiredBody')}>{t('confirm.expiredTitle')}</Title>
        <Cta
          label={t('extend.backToDuration')}
          onClick={() => void navigate({ to: '/sessions/$sessionId/extend', params: { sessionId } })}
        />
      </Page>
    );
  }

  const requested = quote.additionalRequestedMinutes ?? 0;
  const entitlement = quote.additionalEntitlementMinutes ?? quote.additionalMinutes ?? 0;
  const newExpiry = new Date(new Date(session.data.expiresAt).getTime() + entitlement * 60_000);
  const free = quote.chargeCents === 0;

  return (
    <>
      <TopBar title={t('extend.confirmTitle')} />
      <Screen>
        {/* The top bar already carries the confirm title — no body repeat
            (review §5). */}
        <div className="flex flex-col gap-3.5 rounded-lg bg-surface-muted p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-[13px] text-muted">{t('extend.chargeNow')}</span>
            <span className="num text-4xl font-bold">{usd(quote.chargeCents)}</span>
          </div>
          <div className="flex flex-col gap-2 border-t border-hairline pt-3">
            <Row label={t('extend.requestedAdditional')} value={durationLabel(requested, t)} />
            <Row label={t('extend.validAdditional')} value={durationLabel(entitlement, t)} />
            <Row
              label={t('extend.newEndTime')}
              value={lotExpiryLabel(
                newExpiry,
                session.data.lot.timezone,
                resolveLotTimeLanguage(i18n.language),
                t,
              )}
              mono
            />
            <Row label={t('extend.totalDuration')} value={durationLabel(quote.entitlementMinutes, t)} />
            <Row label={t('extend.alreadyPaid')} value={usd(session.data.paidTotalCents)} mono />
            <Row
              label={t('extend.cumulativePaid')}
              value={usd(session.data.paidTotalCents + quote.chargeCents)}
              mono
            />
          </div>
        </div>
        <div className="num flex justify-between text-[11px] text-muted">
          <span>#{quote.id.slice(0, 8).toUpperCase()}</span>
          <span>{countdown(remaining)}</span>
        </div>
        <Note>{free ? t('extend.freeNote') : t('extend.paidNote')}</Note>
        <Cta
          label={free ? t('extend.freeCta') : t('extend.payCta')}
          note={free ? t('extend.free') : t('confirm.payNote', { amount: usd(quote.chargeCents) })}
          onClick={() => void navigate({ to: '/pay', search: { quoteId: quote.id, kind: quote.kind } })}
        />
      </Screen>
    </>
  );
}

function Page({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <>
      <TopBar title={t('extend.title')} />
      <Screen>{children}</Screen>
    </>
  );
}
